import type { EnvironmentConfig } from '@/types';

const trimSlash = (url: string) => url.replace(/\/$/, '');

/** Build-time app env from Vite (`development` | `staging` | `production`). */
export const appEnv =
  (import.meta.env.VITE_APP_ENV as 'development' | 'staging' | 'production') ||
  'development';

const viteApiUrl = import.meta.env.VITE_API_URL
  ? trimSlash(import.meta.env.VITE_API_URL)
  : undefined;
const viteWsUrl = import.meta.env.VITE_WS_URL
  ? trimSlash(import.meta.env.VITE_WS_URL)
  : undefined;

/** Placeholder API origins for the selector; this build's env uses Vite URLs. */
const FALLBACK_URLS = {
  staging: {
    apiUrl: 'https://api-staging.example.com',
    wsUrl: 'wss://api-staging.example.com',
  },
  production: {
    apiUrl: 'https://api.example.com',
    wsUrl: 'wss://api.example.com',
  },
  development: {
    apiUrl: 'http://localhost:3000',
    wsUrl: 'ws://localhost:3000',
  },
} as const;

function urlsFor(id: keyof typeof FALLBACK_URLS) {
  const fallback = FALLBACK_URLS[id];
  if (appEnv === id && viteApiUrl) {
    return { apiUrl: viteApiUrl, wsUrl: viteWsUrl || fallback.wsUrl };
  }
  return fallback;
}

export const environments: EnvironmentConfig[] = [
  {
    id: 'staging',
    name: 'Staging Environment',
    ...urlsFor('staging'),
    isProduction: false,
    features: {
      realTimeLogs: true,
      advancedAnalytics: true,
      queueManagement: true,
    },
    limits: {
      maxApiCalls: 10000,
      maxUsers: 50,
      retentionDays: 30,
    },
  },
  {
    id: 'production',
    name: 'Production Environment',
    ...urlsFor('production'),
    isProduction: true,
    features: {
      realTimeLogs: true,
      advancedAnalytics: true,
      queueManagement: true,
    },
    limits: {
      maxApiCalls: 1000000,
      maxUsers: 1000,
      retentionDays: 365,
    },
  },
  {
    id: 'development',
    name: 'Development Environment',
    ...urlsFor('development'),
    isProduction: false,
    // CSRF is enabled - backend requires CSRF tokens for state-changing operations
    // The API client will automatically fetch and include CSRF tokens
    features: {
      realTimeLogs: true,
      advancedAnalytics: false,
      queueManagement: true,
    },
    limits: {
      maxApiCalls: 1000,
      maxUsers: 10,
      retentionDays: 7,
    },
  },
];

/**
 * Deployed staging/production builds are pinned to their own backend so the
 * header selector cannot cross-call the other Salesforce org. Local development
 * can still switch between all environments.
 */
export const isEnvironmentPinned = appEnv === 'staging' || appEnv === 'production';

export const defaultEnvironment =
  environments.find((env) => env.id === appEnv) ??
  environments.find((env) => env.id === 'development') ??
  environments[2];

/** Environments available in the UI selector for this build. */
export const selectableEnvironments = isEnvironmentPinned
  ? environments.filter((env) => env.id === appEnv)
  : environments;

/** Zustand persist keys — namespaced per app env even on separate origins. */
export const DASHBOARD_STORAGE_KEY = `dashboard-storage-${appEnv}`;
export const AUTH_STORAGE_KEY = `auth-storage-${appEnv}`;
